import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  POST_HOLD_MONITOR_SECONDS,
  PURGE_MAX_BREATHS,
  REQUIRED_RECOVERY_BREATHS,
} from '../data/apneaProtocols';

/**
 * The apnea session phase machine.
 *
 * All timing is derived from `Date.now()` deltas against a phase-start timestamp.
 * The interval exists only to trigger re-renders — it is never accumulated. This
 * matters: browsers throttle `setInterval` heavily in backgrounded tabs, and a
 * tick-accumulating timer would silently desynchronise over a 40-minute table.
 * With timestamp deltas, a tab that was backgrounded for two minutes resumes
 * showing the correct elapsed time and the correct phase.
 *
 * Holds count UP, never down. The sources are explicit that the timer is not in
 * charge; a countdown invites racing the clock.
 */

export type ApneaPhaseKind =
  | 'idle'
  | 'breatheUp'
  | 'purge'
  | 'peakInhalation'
  | 'hold'
  | 'recovery'
  | 'monitor'
  | 'rest'
  | 'complete';

export interface ApneaTimerSet {
  /** Prescribed hold, in seconds. Null for user-ended holds. */
  holdSeconds: number | null;
  /** Rest/ventilation before the next hold, in seconds. */
  ventSeconds: number;
}

export interface ApneaTimerConfig {
  sets: ApneaTimerSet[];
  /** Ventilation before the first hold. */
  breatheUpSeconds: number;
  /** NAUI permits 3-5; 0 disables. Capped at PURGE_MAX_BREATHS. */
  purgeBreaths?: number;
  /** Prompt through the segmented peak inhalation before each hold. */
  includePeakInhalation?: boolean;
}

export interface RecordedSet {
  set: number;
  holdSeconds: number;
  ventSeconds: number;
  firstContractionAt: number | null;
  endedEarly: boolean;
  recoveryBreathsCompleted: number;
}

export interface ApneaTimerState {
  phase: ApneaPhaseKind;
  /** 1-based; 0 while idle. */
  setNumber: number;
  totalSets: number;
  /** Seconds elapsed in the current phase. */
  phaseElapsed: number;
  /** Seconds left in a fixed-duration phase; null for open-ended phases. */
  phaseRemaining: number | null;
  /** Target hold for this set, if prescribed. */
  holdTarget: number | null;
  /** Seconds past the hold target; 0 when at or under. */
  overTarget: number;
  /** Elapsed-at-tap for the current hold's first contraction. */
  firstContractionAt: number | null;
  /** Completed breaths in a counted phase (purge, recovery). */
  breathCount: number;
  breathTarget: number;
  /** Total session elapsed, across all phases. */
  sessionElapsed: number;
  isRunning: boolean;
  isPaused: boolean;
  /** What comes next, so the user is never guessing. */
  nextPhase: ApneaPhaseKind | null;
  records: RecordedSet[];
}

export interface ApneaTimerControls {
  start: () => void;
  /** End the current hold and move to recovery breathing. */
  endHold: () => void;
  /** Mark the first contraction at the current hold elapsed. */
  markContraction: () => void;
  /** Advance a self-paced phase (peak inhalation, purge, recovery breaths). */
  advance: () => void;
  /** Pause — unavailable during a hold, which is user-ended anyway. */
  pause: () => void;
  resume: () => void;
  /** Abandon the remaining sets. Records what was completed. */
  abort: (reason?: string) => void;
  /** Skip the rest of this set and move to the next. */
  skipSet: () => void;
  reset: () => void;
  /** Resume from a persisted snapshot. Returns false if it does not fit this session. */
  restore: (snapshot: ApneaTimerSnapshot) => boolean;
}

const TICK_MS = 200;

/** Phases whose duration is fixed and therefore counted down. */
const isTimedPhase = (phase: ApneaPhaseKind): boolean =>
  phase === 'breatheUp' || phase === 'monitor' || phase === 'rest';

export interface UseApneaTimerOptions extends ApneaTimerConfig {
  /** Injectable clock, for tests. */
  now?: () => number;
  onPhaseChange?: (phase: ApneaPhaseKind, setNumber: number) => void;
  onComplete?: (records: RecordedSet[]) => void;
  /**
   * localStorage key for crash recovery. When set, in-progress state is written on
   * every change and can be restored after a reload. Omit to disable persistence.
   */
  persistKey?: string;
}

/**
 * Snapshot written to localStorage. Timestamps are absolute wall-clock values, so a
 * restored session resumes with the elapsed time that actually passed rather than
 * pretending the gap did not happen.
 */
export interface ApneaTimerSnapshot {
  version: 1;
  phase: ApneaPhaseKind;
  setIndex: number;
  phaseStart: number | null;
  sessionStart: number | null;
  pausedAt: number | null;
  breathCount: number;
  firstContractionAt: number | null;
  records: RecordedSet[];
  lastHold: { seconds: number; endedEarly: boolean } | null;
  /** Guards against restoring a snapshot into a differently-shaped session. */
  setCount: number;
  savedAt: number;
}

/** How long a snapshot stays offerable. Beyond this it is stale, not a resume. */
export const SNAPSHOT_MAX_AGE_MS = 6 * 3600_000;

/**
 * Read a resumable snapshot. `now` is injectable so tests can use the same mock
 * clock the hook does — otherwise a snapshot stamped by a mock clock always reads
 * as stale against the real one.
 */
export const readSnapshot = (
  key: string,
  now: () => number = () => Date.now()
): ApneaTimerSnapshot | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ApneaTimerSnapshot;
    if (parsed.version !== 1) return null;
    if (parsed.phase === 'idle' || parsed.phase === 'complete') return null;
    if (now() - parsed.savedAt > SNAPSHOT_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const clearSnapshot = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Storage unavailable; nothing to clear.
  }
};

export const useApneaTimer = (options: UseApneaTimerOptions) => {
  const {
    sets,
    breatheUpSeconds,
    purgeBreaths = 0,
    includePeakInhalation = true,
    now = () => Date.now(),
    onPhaseChange,
    onComplete,
    persistKey,
  } = options;

  const cappedPurge = Math.min(Math.max(0, purgeBreaths), PURGE_MAX_BREATHS);

  const [phase, setPhase] = useState<ApneaPhaseKind>('idle');
  const [setIndex, setSetIndex] = useState(0);
  const [phaseStart, setPhaseStart] = useState<number | null>(null);
  const [sessionStart, setSessionStart] = useState<number | null>(null);
  const [pausedAt, setPausedAt] = useState<number | null>(null);
  const [breathCount, setBreathCount] = useState(0);
  const [firstContractionAt, setFirstContractionAt] = useState<number | null>(null);
  const [records, setRecords] = useState<RecordedSet[]>([]);
  const [, forceRender] = useState(0);

  // Held in a ref so the recorded hold survives the phase transition that reads it.
  const lastHoldRef = useRef<{ seconds: number; endedEarly: boolean } | null>(null);

  const currentSet = sets[setIndex];
  const holdTarget = currentSet?.holdSeconds ?? null;

  // Re-render loop. Display only — never the source of elapsed time.
  useEffect(() => {
    if (phase === 'idle' || phase === 'complete' || pausedAt !== null) return undefined;
    const id = window.setInterval(() => forceRender(n => n + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, [phase, pausedAt]);

  const elapsedIn = useCallback((from: number | null): number => {
    if (from === null) return 0;
    const reference = pausedAt ?? now();
    return Math.max(0, Math.floor((reference - from) / 1000));
  }, [pausedAt, now]);

  const phaseElapsed = elapsedIn(phaseStart);
  const sessionElapsed = elapsedIn(sessionStart);

  const phaseDuration = useMemo((): number | null => {
    switch (phase) {
      case 'breatheUp': return breatheUpSeconds;
      case 'monitor': return POST_HOLD_MONITOR_SECONDS;
      case 'rest': return currentSet?.ventSeconds ?? 0;
      default: return null;
    }
  }, [phase, breatheUpSeconds, currentSet]);

  const phaseRemaining = phaseDuration === null
    ? null
    : Math.max(0, phaseDuration - phaseElapsed);

  const goTo = useCallback((next: ApneaPhaseKind, nextSetIndex?: number) => {
    const targetIndex = nextSetIndex ?? setIndex;
    setPhase(next);
    setSetIndex(targetIndex);
    setPhaseStart(now());
    setBreathCount(0);
    if (next === 'hold') setFirstContractionAt(null);
    onPhaseChange?.(next, targetIndex + 1);
  }, [setIndex, now, onPhaseChange]);

  /** The phase that follows the current one, given the config. */
  const nextPhase = useMemo((): ApneaPhaseKind | null => {
    switch (phase) {
      case 'breatheUp':
        if (cappedPurge > 0) return 'purge';
        return includePeakInhalation ? 'peakInhalation' : 'hold';
      case 'purge':
        return includePeakInhalation ? 'peakInhalation' : 'hold';
      case 'peakInhalation': return 'hold';
      case 'hold': return 'recovery';
      case 'recovery': return 'monitor';
      case 'monitor':
        return setIndex + 1 < sets.length ? 'rest' : 'complete';
      case 'rest':
        return cappedPurge > 0 ? 'purge' : (includePeakInhalation ? 'peakInhalation' : 'hold');
      default: return null;
    }
  }, [phase, cappedPurge, includePeakInhalation, setIndex, sets.length]);

  const recordHold = useCallback((seconds: number, endedEarly: boolean, breaths: number) => {
    setRecords(prev => [...prev, {
      set: setIndex + 1,
      holdSeconds: seconds,
      ventSeconds: currentSet?.ventSeconds ?? 0,
      firstContractionAt,
      endedEarly,
      recoveryBreathsCompleted: breaths,
    }]);
  }, [setIndex, currentSet, firstContractionAt]);

  // Auto-advance the fixed-duration phases when their time is up.
  useEffect(() => {
    if (pausedAt !== null || !isTimedPhase(phase) || phaseDuration === null) return;
    if (phaseElapsed < phaseDuration) return;

    if (phase === 'breatheUp' || phase === 'rest') {
      goTo(nextPhase ?? 'hold');
    } else if (phase === 'monitor') {
      if (setIndex + 1 < sets.length) {
        // The monitor phase closes out a set, so advance the index here —
        // the upcoming rest belongs to the next set, not the one just finished.
        goTo('rest', setIndex + 1);
      } else {
        setPhase('complete');
        onComplete?.(records);
      }
    }
  }, [phase, phaseElapsed, phaseDuration, pausedAt, nextPhase, goTo, setIndex, sets.length, records, onComplete]);

  // A prescribed hold auto-ends at its target; the user can also end it early.
  useEffect(() => {
    if (phase !== 'hold' || pausedAt !== null || holdTarget === null) return;
    if (phaseElapsed < holdTarget) return;
    lastHoldRef.current = { seconds: phaseElapsed, endedEarly: false };
    goTo('recovery');
  }, [phase, phaseElapsed, holdTarget, pausedAt, goTo]);

  const start = useCallback(() => {
    const t = now();
    setSessionStart(t);
    setPhaseStart(t);
    setRecords([]);
    setSetIndex(0);
    setBreathCount(0);
    setFirstContractionAt(null);
    setPausedAt(null);
    setPhase('breatheUp');
    onPhaseChange?.('breatheUp', 1);
  }, [now, onPhaseChange]);

  const endHold = useCallback(() => {
    if (phase !== 'hold') return;
    lastHoldRef.current = {
      seconds: phaseElapsed,
      endedEarly: holdTarget !== null && phaseElapsed < holdTarget,
    };
    goTo('recovery');
  }, [phase, phaseElapsed, holdTarget, goTo]);

  const markContraction = useCallback(() => {
    if (phase !== 'hold' || firstContractionAt !== null) return;
    setFirstContractionAt(phaseElapsed);
  }, [phase, phaseElapsed, firstContractionAt]);

  const advance = useCallback(() => {
    if (phase === 'peakInhalation') {
      goTo('hold');
      return;
    }

    if (phase === 'purge') {
      const next = breathCount + 1;
      if (next >= cappedPurge) {
        goTo(includePeakInhalation ? 'peakInhalation' : 'hold');
      } else {
        setBreathCount(next);
      }
      return;
    }

    if (phase === 'recovery') {
      const next = breathCount + 1;
      if (next >= REQUIRED_RECOVERY_BREATHS) {
        const hold = lastHoldRef.current;
        recordHold(hold?.seconds ?? 0, hold?.endedEarly ?? false, REQUIRED_RECOVERY_BREATHS);
        lastHoldRef.current = null;
        goTo('monitor');
      } else {
        setBreathCount(next);
      }
    }
  }, [phase, breathCount, cappedPurge, includePeakInhalation, goTo, recordHold]);

  const pause = useCallback(() => {
    // Pausing a breath-hold is meaningless, and the hold is user-ended anyway.
    if (phase === 'hold' || phase === 'idle' || phase === 'complete') return;
    if (pausedAt === null) setPausedAt(now());
  }, [phase, pausedAt, now]);

  const resume = useCallback(() => {
    if (pausedAt === null) return;
    const pausedFor = now() - pausedAt;
    setPhaseStart(prev => (prev === null ? prev : prev + pausedFor));
    setSessionStart(prev => (prev === null ? prev : prev + pausedFor));
    setPausedAt(null);
  }, [pausedAt, now]);

  const abort = useCallback((_reason?: string) => {
    if (phase === 'hold') {
      recordHold(phaseElapsed, true, 0);
    }
    lastHoldRef.current = null;
    setPhase('complete');
    setPausedAt(null);
  }, [phase, phaseElapsed, recordHold]);

  const skipSet = useCallback(() => {
    if (phase === 'idle' || phase === 'complete') return;
    if (setIndex + 1 >= sets.length) {
      setPhase('complete');
      onComplete?.(records);
      return;
    }
    lastHoldRef.current = null;
    goTo('rest', setIndex + 1);
  }, [phase, setIndex, sets.length, goTo, records, onComplete]);

  const reset = useCallback(() => {
    setPhase('idle');
    setSetIndex(0);
    setPhaseStart(null);
    setSessionStart(null);
    setPausedAt(null);
    setBreathCount(0);
    setFirstContractionAt(null);
    setRecords([]);
    lastHoldRef.current = null;
    if (persistKey) clearSnapshot(persistKey);
  }, [persistKey]);

  /**
   * Restore a snapshot. Rejected if the session shape changed (different set count),
   * since resuming into a different table would silently mis-record the session.
   */
  const restore = useCallback((snapshot: ApneaTimerSnapshot): boolean => {
    if (snapshot.setCount !== sets.length) return false;
    setPhase(snapshot.phase);
    setSetIndex(snapshot.setIndex);
    setPhaseStart(snapshot.phaseStart);
    setSessionStart(snapshot.sessionStart);
    setPausedAt(snapshot.pausedAt);
    setBreathCount(snapshot.breathCount);
    setFirstContractionAt(snapshot.firstContractionAt);
    setRecords(snapshot.records);
    lastHoldRef.current = snapshot.lastHold;
    return true;
  }, [sets.length]);

  // Persist on every meaningful change. Cheap (one small JSON write) next to the
  // cost of losing a 40-minute session to an accidental reload.
  useEffect(() => {
    if (!persistKey) return;
    if (phase === 'idle' || phase === 'complete') {
      clearSnapshot(persistKey);
      return;
    }
    try {
      const snapshot: ApneaTimerSnapshot = {
        version: 1,
        phase,
        setIndex,
        phaseStart,
        sessionStart,
        pausedAt,
        breathCount,
        firstContractionAt,
        records,
        lastHold: lastHoldRef.current,
        setCount: sets.length,
        savedAt: now(),
      };
      window.localStorage.setItem(persistKey, JSON.stringify(snapshot));
    } catch {
      // Storage full or unavailable — the session continues, just without recovery.
    }
  }, [
    persistKey, phase, setIndex, phaseStart, sessionStart, pausedAt,
    breathCount, firstContractionAt, records, sets.length, now,
  ]);

  const breathTarget = phase === 'purge'
    ? cappedPurge
    : phase === 'recovery' ? REQUIRED_RECOVERY_BREATHS : 0;

  const state: ApneaTimerState = {
    phase,
    setNumber: phase === 'idle' ? 0 : setIndex + 1,
    totalSets: sets.length,
    phaseElapsed,
    phaseRemaining,
    holdTarget,
    overTarget: phase === 'hold' && holdTarget !== null
      ? Math.max(0, phaseElapsed - holdTarget)
      : 0,
    firstContractionAt,
    breathCount,
    breathTarget,
    sessionElapsed,
    isRunning: phase !== 'idle' && phase !== 'complete',
    isPaused: pausedAt !== null,
    nextPhase,
    records,
  };

  const controls: ApneaTimerControls = {
    start, endHold, markContraction, advance, pause, resume, abort, skipSet, reset, restore,
  };

  return { state, controls };
};
